import React from 'react'
import PropTypes from 'prop-types'
import {Col, FundsSentIcon, H3, RepaymentIcon, Row, ButtonPrimary} from '@modifi/ui'
import {landGreen, red} from '@modifi/colors'
import {FormattedDate, FormattedNumber} from '@modifi/plugin-i18n'
import {Link} from '@modifi/router'
import {Table, TableProgressBar, Td, TdWithWordBreak, Th, Tr} from '../../../../ui'
import BankCard from '../../../shared/components/bank-card'
import {accountConfigShape} from '../../constants'
import {DIALOG_NAME as DIALOG_SPLIT_TRANSACTION} from '../../dialogs/split-transaction-dialog'
import {RightAlignedTd, StyledButtonLink, StyledComment} from './styles'
import {URL_PATH_TRANSACTION_DETAILS} from '../../../../routes/constants'
import {TransactionStatusTag} from '../../../../components/transaction-status-tag'

export default class TransactionList extends React.Component {
  static propTypes = {
    // state
    transactionList: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.string,
        currency: PropTypes.string,
        id: PropTypes.number,
        processedOn: PropTypes.string,
        purpose: PropTypes.string,
        recipientBankAccountHolder: PropTypes.string,
        recipientBankAccountNumber: PropTypes.string,
        recipientBankCode: PropTypes.string,
        senderBankAccountHolder: PropTypes.string,
        senderBankAccountNumber: PropTypes.string,
        senderBankCode: PropTypes.string,
        senderBankName: PropTypes.string,
        valueDate: PropTypes.string,
        accountConfig: PropTypes.shape(accountConfigShape).isRequired,
        transactionType: PropTypes.string,
        actions: PropTypes.shape({
          canRemoveAsSpvTransaction: PropTypes.bool,
          canSplitTransaction: PropTypes.bool,
        }),
        tag: PropTypes.shape({
          transactionCategory: PropTypes.string,
          additionalComment: PropTypes.string,
        }),
        isPassThrough: PropTypes.bool.isRequired,
      })
    ),
    progress: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
    isRelevant: PropTypes.bool.isRequired,

    // dispatch
    fetchTransactions: PropTypes.func.isRequired,
    showDialog: PropTypes.func.isRequired,
    progressStart: PropTypes.func.isRequired,
    progressDone: PropTypes.func.isRequired,
    setRelevantFlag: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  }

  static defaultProps = {
    transactionList: [],
  }

  componentDidMount() {
    const {fetchTransactions, isRelevant} = this.props
    fetchTransactions({isRelevant})
  }

  componentDidUpdate(prevProps) {
    const {isRelevant, fetchTransactions, loading, progressStart, progressDone} = this.props
    if (!prevProps.loading && loading) {
      progressStart()
    } else if (prevProps.loading && !loading) {
      progressDone()
    }
    if (isRelevant !== prevProps.isRelevant) fetchTransactions({isRelevant})
  }

  render() {
    const {progress, transactionList, showDialog, isRelevant, setRelevantFlag, t} = this.props

    return (
      <>
        <H3 my={30}>{t('unmatched-transactions.page.title')}</H3>
        <Row mb={30}>
          <Col auto>
            <StyledButtonLink
              onClick={() => setRelevantFlag(true)}
              selected={isRelevant}
              active={!isRelevant}
              transparent
            >
              {t('unmatched-transactions.page.filter-relevant.cta')}
            </StyledButtonLink>
          </Col>
          <Col auto>
            <StyledButtonLink
              onClick={() => setRelevantFlag(false)}
              selected={!isRelevant}
              active={isRelevant}
              transparent
            >
              {t('unmatched-transactions.page.filter-all.cta')}
            </StyledButtonLink>
          </Col>
        </Row>
        <Table>
          <thead>
            <Tr>
              <Th>&nbsp;</Th>
              <Th>{t('unmatched-transactions.page.table-header.import-date.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.amount.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.value-date.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.sender-bank.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.recipient-bank.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.purpose.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.transaction-category.title')}</Th>
              <Th>{t('unmatched-transactions.page.table-header.actions.title')}</Th>
            </Tr>
          </thead>

          <tbody>
            <TableProgressBar columns={10} progress={progress} />
            {transactionList.length === 0 && (
              <Tr>
                <Td>&nbsp;</Td>
              </Tr>
            )}

            {transactionList.length > 0 &&
              transactionList.map(transaction => {
                const {
                  id,
                  amount,
                  currency,
                  importDate,
                  senderBankAccountHolder,
                  valueDate,
                  senderBankName,
                  senderBankAccountNumber,
                  senderBankCode,
                  recipientBankAccountHolder,
                  recipientBankAccountNumber,
                  recipientBankCode,
                  purpose,
                  accountConfig: {
                    productType,
                    refinancingProvider1,
                    refinancingProvider2,
                    bankProvider,
                  },
                  tag,
                } = transaction

                return (
                  <Tr
                    key={id}
                    data-ident="transaction-row"
                    data-split-status={transaction?.actions?.canSplitTransaction}
                    data-spv-removal-status={transaction?.actions?.canRemoveAsSpvTransaction}
                    data-is-pass-through={transaction?.isPassThrough}
                  >
                    <Td>
                      {amount > 0 ? (
                        <RepaymentIcon height={16} width={16} color={landGreen} />
                      ) : (
                        <FundsSentIcon height={16} width={16} color={red} />
                      )}
                    </Td>
                    <Td>
                      <FormattedDate>{importDate}</FormattedDate>
                    </Td>
                    <Td>
                      {amount < 0 && '-'}
                      <FormattedNumber currency={currency}>{Math.abs(amount)}</FormattedNumber>
                    </Td>
                    <Td>
                      <FormattedDate>{valueDate}</FormattedDate>
                    </Td>
                    <Td>
                      <BankCard
                        bankName={senderBankName}
                        bankAccountHolder={senderBankAccountHolder}
                        bankCode={senderBankCode}
                        bankAccountNumber={senderBankAccountNumber}
                      />
                    </Td>
                    <Td>
                      <BankCard
                        bankAccountHolder={recipientBankAccountHolder}
                        bankCode={recipientBankCode}
                        bankAccountNumber={recipientBankAccountNumber}
                        refinancingProvider1={refinancingProvider1}
                        refinancingProvider2={refinancingProvider2}
                        bankProvider={bankProvider}
                        productType={productType}
                      />
                    </Td>
                    <TdWithWordBreak>{purpose}</TdWithWordBreak>
                    <Td>
                      <TransactionStatusTag status={tag?.transactionCategory} />
                      <StyledComment>{tag?.additionalComment}</StyledComment>
                    </Td>
                    <RightAlignedTd>
                      <Row>
                        <Col auto mt={5}>
                          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                          <Link pattern={URL_PATH_TRANSACTION_DETAILS} params={{id}}>
                            <ButtonPrimary
                              data-ident="button"
                              data-target="transaction-details"
                              data-type="link"
                              bordered
                              transparent
                              small
                            >
                              <span>{t('unmatched-transactions.page.view.cta')}</span>
                            </ButtonPrimary>
                          </Link>
                        </Col>
                        {transaction?.actions?.canSplitTransaction && (
                          <Col auto mt={5}>
                            <ButtonPrimary
                              data-ident="button"
                              data-target="split-transaction"
                              data-type="link"
                              onClick={() => showDialog(DIALOG_SPLIT_TRANSACTION, {transaction})}
                              bordered
                              transparent
                              small
                            >
                              {t('unmatched-transactions.page.split.cta')}
                            </ButtonPrimary>
                          </Col>
                        )}
                      </Row>

                      {/* Not removing the code since this is not clear if it's still been used */}
                      {/* {transaction?.actions?.canRemoveAsSpvTransaction && ( */}
                      {/*  <ButtonSecondary */}
                      {/*    data-ident="button" */}
                      {/*    data-target="spv-removal" */}
                      {/*    data-type="link" */}
                      {/*    onClick={() => showDialog(DIALOG_SPV_REMOVAL, {id})} */}
                      {/*    inverse */}
                      {/*  > */}
                      {/*    {t('unmatched-transactions.page.spv.cta')} */}
                      {/*  </ButtonSecondary> */}
                      {/* )} */}
                    </RightAlignedTd>
                  </Tr>
                )
              })}
          </tbody>
        </Table>
      </>
    )
  }
}
